import { PanelLeftOpen } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { DgbLogo, DgbSymbol } from '@/components/brand-logo';
import { currentEnvironment, ENVIRONMENT_LABEL_KEY } from '../environment';
import { ShellFooter } from '../footer/ShellFooter';
import type { ActiveLocation } from '../model/active-location';
import type { ResolvedModule } from '../model/resolve-navigation';
import { AccountMenu } from './AccountMenu';
import { DrawerPreferences } from './DrawerPreferences';
import { ModuleRail } from './ModuleRail';
import { RailToggle } from './RailToggle';

/**
 * The rail, whole: brand, the two-level module navigation, and the account disclosure at the foot.
 *
 * `variant` is the ONLY difference between the persistent rail and the drawer's contents, and it
 * changes the outer box plus what only one of the two can use: the collapse controls belong to the
 * persistent rail, the display preferences and the footer to the drawer. Everything else is the
 * same element tree, so a change to the navigation cannot land in one and miss the other. (The
 * previous shell achieved the same thing by sharing a `navContent` variable, but had to duplicate
 * the session-controls block for the drawer, which is how the two came to differ in what they
 * showed.)
 *
 * The brand is a LINK to `/`. It was not one before: nothing in the old shell pointed at the root
 * screen, so the dashboard was reachable only by typing the URL or pressing Back. `home` is also a
 * rail entry now, which is the explicit route; the mark being clickable is the convention people
 * try first.
 *
 * THE MARK IS THE KIT'S (decision 51): composition 10, 20px tall, with the environment chip beside
 * it (Brand Kit § The frame). The compressed rail shows composition 06, the symbol alone;
 * `shell.css` decides which of the two is visible, because a component cannot read its own
 * container's width. The product's name, DGB Console, is in the page title and the footer.
 *
 * THE ENVIRONMENT CHIP IS THERE IN EVERY ENVIRONMENT, production included, in the colour that tells
 * them apart: a write to staging must never look like one to production, and a chip that appears
 * only outside production would make its absence the signal.
 *
 * In the drawer the footer comes along: on a phone the page footer is hidden, and the drawer — the
 * bottom bar's "Altro" — is where its links and its version live instead.
 *
 * EXPANDING A RAIL THAT WIDTH COMPRESSED. Below 1180px the rail is narrow because the window is,
 * and the standard's rule applies: "the user's explicit preference beats the automatic behaviour"
 * and "if the sidebar cannot narrow the content in compact mode, it opens overlaid". So the expand
 * control is there in that band too, and it opens the drawer — the same expanded rail, over the
 * content rather than beside it. `shell.css` shows one of the two expand buttons, never both.
 */
export function AppRail({
  variant,
  modules,
  active,
  onNavigate,
  onToggleRail,
  onOpenDrawer,
  drawerOpen = false,
}: {
  variant: 'fixed' | 'drawer';
  modules: readonly ResolvedModule[];
  active: ActiveLocation;
  onNavigate?: () => void;
  /** The persistent rail's collapse control; the drawer passes nothing and gets none. */
  onToggleRail?: () => void;
  /** Where a rail compressed by width expands to: the drawer, overlaid. */
  onOpenDrawer?: () => void;
  drawerOpen?: boolean;
}) {
  const { t } = useTranslation('shell');
  const environment = currentEnvironment();
  const collapseRef = useRef<HTMLButtonElement>(null);
  const expandRef = useRef<HTMLButtonElement>(null);

  // The button pressed is the one `shell.css` hides next, so focus moves to its twin once the new
  // `data-rail` has been committed and styled — the next frame.
  function toggleAndRefocus(next: 'collapsed' | 'expanded') {
    onToggleRail?.();
    requestAnimationFrame(() => {
      (next === 'collapsed' ? expandRef : collapseRef).current?.focus();
    });
  }

  const showToggles = variant === 'fixed' && onToggleRail !== undefined;

  return (
    <div className={variant === 'fixed' ? 'app-rail' : 'rail-drawer'}>
      <div className="rail-top">
        <Link to="/" onClick={onNavigate} aria-label={t('nav.home')} className="rail-brand">
          {/* `alt=""`: the link's own name is the destination, and the mark is its picture. */}
          <DgbLogo className="rail-logo" alt="" />
          <DgbSymbol className="rail-symbol" alt="" />
        </Link>
        <span className="rail-env" data-env={environment}>
          {t(ENVIRONMENT_LABEL_KEY[environment])}
        </span>
        {showToggles ? (
          <RailToggle
            ref={collapseRef}
            collapsed={false}
            onToggle={() => toggleAndRefocus('collapsed')}
          />
        ) : null}
      </div>
      <ModuleRail modules={modules} active={active} {...(onNavigate ? { onNavigate } : {})} />
      {variant === 'drawer' ? <DrawerPreferences /> : null}
      {showToggles ? (
        <div className="rail-expand">
          <RailToggle
            ref={expandRef}
            className="rail-expand-choice"
            collapsed
            onToggle={() => toggleAndRefocus('expanded')}
          />
          {onOpenDrawer === undefined ? null : (
            <button
              type="button"
              className="rail-toggle rail-expand-overlay"
              aria-label={t('rail.expand')}
              title={t('rail.expand')}
              aria-controls="app-nav-drawer"
              aria-expanded={drawerOpen}
              onClick={onOpenDrawer}
            >
              <PanelLeftOpen aria-hidden="true" />
            </button>
          )}
        </div>
      ) : null}
      <AccountMenu />
      {variant === 'drawer' ? (
        <ShellFooter variant="drawer" {...(onNavigate ? { onNavigate } : {})} />
      ) : null}
    </div>
  );
}
